import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { analyticsApi } from '../../api/analytics';
import { workspacesApi, getWorkspaceId, type WorkspaceInfo } from '../../api/workspaces';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useDrillStack } from '../../hooks/useDrillStack';
import { seedViewData, ASK_VIEW_EVENT, ASK_TURN_EVENT } from '../../api/askChat';
import type { DrillView } from '../../components/analytics/drillView';
import ViewRenderer from '../../components/analytics/ViewRenderer';
import SparklesIcon from '../../components/analytics/SparklesIcon';
import SyncSplash from '../../components/analytics/SyncSplash';

/**
 * Ask Analytics — the CANVAS half of the one-chat experience.
 *
 * The conversation is the AI Copilot buddy (CopilotPanel in askMode): it
 * starts centered and docks right after the first turn. This page renders
 * whatever the buddy composes — full-screen, tabbed, drillable — by listening
 * to the `a5suite:ask-view` / `a5suite:ask-turn` window events (datasets
 * arrive pre-fetched from the bridge and seed the react-query cache).
 * Clicking inside the view drills deterministically; the LLM is never in the
 * click path.
 */

interface TurnView {
  view: DrillView;
}

export default function AskPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enableAnalytics, loading: flagsLoading } = useFeatureFlags();
  const workspaceId = getWorkspaceId();

  // The canvas STARTS as the dashboard — the centered chat floats over it.
  const [turnViews, setTurnViews] = useState<TurnView[]>([{ view: { kind: 'inbox' } }]);
  const [activeViewIndex, setActiveViewIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  const { data: workspaces } = useQuery({ queryKey: ['workspaces'], queryFn: workspacesApi.list });
  const activeWorkspace: WorkspaceInfo | undefined = workspaces?.find(w => w.isActive);
  const dataAsOf = activeWorkspace?.lastSyncedAtUtc ?? null;
  const awaitingFirstSync = activeWorkspace?.sourceKind === 'mysql' && !dataAsOf;
  useQuery({
    queryKey: ['workspaces', 'sync-poll'],
    queryFn: workspacesApi.list,
    refetchInterval: awaitingFirstSync ? 15_000 : false,
  });

  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status', workspaceId],
    queryFn: () => analyticsApi.aiStatus(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const aiEnabled = !!aiStatus?.aiEnabled;

  const activeTurnView = turnViews[activeViewIndex] ?? turnViews[0];
  const { stack, view: currentDrillView, push, popTo, reset } = useDrillStack({ kind: 'overview' });

  // New turn → take over the screen with its first view.
  useEffect(() => {
    if (activeTurnView) reset(activeTurnView.view);
  }, [turnViews]); // eslint-disable-line react-hooks/exhaustive-deps

  // The buddy's events → views on this canvas.
  useEffect(() => {
    const onView = (e: Event) => {
      const detail = (e as CustomEvent<{ view: DrillView; datasets: { dataRef: string; data: unknown }[] }>).detail;
      if (!detail?.view) return;
      if (detail.datasets) seedViewData(queryClient, detail.datasets);
      setTurnViews(prev => (prev.some(tv => sameView(tv.view, detail.view)) ? prev : [...prev, { view: detail.view }]));
      setActiveViewIndex(prev => prev + 1);
      setBusy(true);
    };
    const onTurn = (e: Event) => {
      const detail = (e as CustomEvent<{ turn: { views: { view: DrillView; datasets: { dataRef: string; data: unknown }[] }[] } }>).detail;
      setBusy(false);
      if (!detail?.turn?.views) return;
      for (const v of detail.turn.views) {
        if (v.datasets) seedViewData(queryClient, v.datasets);
      }
      setTurnViews(detail.turn.views.map(v => ({ view: v.view })));
      setActiveViewIndex(0);
    };
    window.addEventListener(ASK_VIEW_EVENT, onView);
    window.addEventListener(ASK_TURN_EVENT, onTurn);
    return () => {
      window.removeEventListener(ASK_VIEW_EVENT, onView);
      window.removeEventListener(ASK_TURN_EVENT, onTurn);
    };
  }, [queryClient]);

  // The buddy appears (centered while the conversation is fresh).
  useEffect(() => {
    if (aiEnabled) window.dispatchEvent(new CustomEvent('copilot-open'));
  }, [aiEnabled]);

  if (!flagsLoading && !enableAnalytics) {
    navigate('/', { replace: true });
    return null;
  }

  if (!aiEnabled) {
    return (
      <div className="max-w-3xl mx-auto py-16 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('analytics.ask.lockedTitle')}</h1>
        <p className="text-sm text-gray-500 mb-4">{t('analytics.ask.lockedBody')}</p>
        <Link to="/analytics" className="text-sm font-medium text-primary-600 hover:text-primary-800">
          {t('analytics.ask.dashboardLink')} →
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 py-2 border-b border-gray-100">
        <SparklesIcon className="w-5 h-5 text-primary-600" />
        <h1 className="text-lg font-semibold text-gray-900">{t('analytics.ask.title')}</h1>
        <span className="text-xs text-gray-400 hidden sm:inline">
          {dataAsOf ? t('analytics.dataAsOf', { time: new Date(dataAsOf).toLocaleString('lt-LT') }) : ''}
        </span>
        {busy && <span className="text-xs text-primary-500 italic">{t('analytics.ask.statusComposing')}</span>}
        <div className="flex-1" />
        {/* Klauskite AI — bring the buddy back (open) and focus its writer */}
        <button
          onClick={() => {
            window.dispatchEvent(new CustomEvent('copilot-open'));
            window.dispatchEvent(new CustomEvent('copilot-focus'));
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:border-primary-400 transition-colors"
        >
          <SparklesIcon className="w-3.5 h-3.5" />
          {t('analytics.ask.link')}
        </button>
      </div>

      {/* Canvas */}
      <div className="p-4 lg:p-6">
        {awaitingFirstSync ? (
          <SyncSplash />
        ) : activeTurnView ? (
          <>
            {/* View tabs + drill breadcrumbs */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {turnViews.length > 1 && turnViews.map((v, i) => (
                <button
                  key={i}
                  onClick={() => { setActiveViewIndex(i); reset(v.view); }}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    i === activeViewIndex
                      ? 'bg-primary-100 text-primary-800 font-medium'
                      : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-300'
                  }`}
                >
                  {t(`analytics.view.${v.view.kind}`)}
                </button>
              ))}
              <div className="flex-1" />
              {stack.length > 1 && (
                <div className="flex flex-wrap items-center gap-1 text-sm">
                  {stack.map((item, i) => (
                    <span key={i} className="flex items-center gap-1">
                      {i > 0 && <span className="text-gray-300">›</span>}
                      <button
                        onClick={() => popTo(i)}
                        className={`px-2 py-0.5 rounded hover:bg-gray-100 ${i === stack.length - 1 ? 'font-medium text-gray-900' : 'text-gray-500'}`}
                      >
                        {t(`analytics.view.${item.kind}`)}
                        {item.kind === 'customer' && item.personName ? `: ${item.personName}` : ''}
                      </button>
                    </span>
                  ))}
                  <button onClick={() => reset(activeTurnView.view)} className="ml-2 text-xs text-gray-400 hover:text-gray-600 underline">
                    {t('analytics.ask.cardBack')}
                  </button>
                </div>
              )}
            </div>

            <ViewRenderer
              view={currentDrillView}
              onDrill={push}
              explain={false}
              onOpenBanking={() => navigate('/banking')}
              onOpenVat={() => navigate('/reports/vat')}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

function sameView(a: DrillView, b: DrillView): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
